import { useMemo } from "react";
import { useAppDispatch, useAppSelector } from "@/state/redux/hooks";
import { selectViewingRecord, setViewingRecord } from "@/state/redux/slices/ui";
import { deserializeRecord, intervalNames, serializeRecord } from "@/common/record";
import { HGCollectionInfo } from "@/common/types";
import RecordIntervalName from "./RecordIntervalName";

interface CollectionTitleRecordBandProps {
  collection: HGCollectionInfo | undefined;
}

/**
 * The record that *is* the collection being browsed, pinned above its timeline.
 *
 * It sits outside the scrolling list rather than in it, for two reasons that
 * both come straight from the model: a title record does not belong to the
 * collection it titles, so it is never in that list; and its own date may fall
 * outside the range on screen, so a row in the flow would come and go as the
 * user navigates time. What heads a collection should be there whenever the
 * collection is.
 *
 * Renders nothing at all when the collection has no title record — which is most
 * of them.
 */
export default function CollectionTitleRecordBand({
  collection,
}: CollectionTitleRecordBandProps) {
  const dispatch = useAppDispatch();
  const viewingRecord = useAppSelector(selectViewingRecord);

  const serialized = collection?.title_record ?? null;
  // The embedded record comes straight from the API, so unlike the ones in the
  // timeline it has never been through enrichRecords and carries no interval
  // names. They are attached here rather than at the point of display, because
  // clicking the band hands this same record to the preview pane, which reads
  // them too.
  const record = useMemo(() => {
    const deserialized = deserializeRecord(serialized);
    if (!deserialized) return null;
    return { ...deserialized, ...intervalNames(deserialized) };
  }, [serialized]);

  if (!record) return null;

  const selected = viewingRecord?.id === record.id;
  const timed = "interval" in record && !!record.interval;

  return (
    <div className="shrink-0 border-b border-border bg-secondary/40">
      <button
        type="button"
        onClick={() =>
          dispatch(setViewingRecord(selected ? null : serializeRecord(record)))
        }
        aria-pressed={selected}
        className={`flex w-full items-center gap-3 py-[10px] pl-[18px] pr-[18px] text-left transition-colors hover:bg-row-hover ${
          selected ? "bg-accent" : ""
        }`}
      >
        {/* The accent rule is the whole visual argument: this band is the
            collection speaking for itself, not the first row of its list. */}
        <span
          aria-hidden
          className="h-[26px] w-[3px] shrink-0 rounded-full bg-primary"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className="truncate font-serif text-[14.5px] font-semibold leading-[1.28] text-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {record.title || collection?.title}
          </span>
          {timed && (
            <span className="mt-[2px] font-mono text-[11px] tracking-[.01em]">
              <RecordIntervalName record={record} />
            </span>
          )}
        </span>
        <span className="shrink-0 whitespace-nowrap rounded border border-border bg-card px-2 py-0.5 font-sans text-[10.5px] text-muted-foreground">
          Collection
        </span>
      </button>
    </div>
  );
}
